import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { confirmDialog } from "primereact/confirmdialog";

import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { useQyDeletePerson } from "@core/query/person.query";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useReactToPrint } from "react-to-print";
import { useCallback, useRef, useState } from "react";
import { useFormDropdownPersonContext } from "../form-dropdown-person/form-dropdown-person.context";
import { useQyGetEmployeeScoreAverage } from "@core/query/employee-score.query";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { useQyGetEmployeeLedgerIndividual } from "@core/query/employee-ledger.query";
import { useFilterEmployeeLedgerContext } from "@domain/employee-ledger/filter-employee-ledger/filter-employee-ledger.context";
import { useFilterPersonContext } from "../filter-person/filter-person.context";
import { useQyGetEmployeeDiciplinary } from "@core/query/employee-diciplinary.query";

export function useEditPerson() {
  // LOCAL DECLARATION;
  const {
    selectedPersonData,
    personId,
    isLoading,
    personError,
    formMethod,
    getValues,
  } = useFormDropdownPersonContext();
  const { userIsManagement } = useUserIdentityContext();

  const [qrToken, setQrToken] = useState("");
  const [searchParams] = useSearchParams();
  const { branchId } = useParams();
  const returnParam = searchParams.get("return");
  const navigate = useNavigate();
  const { mapDeletePerson } = useFormApiMapper();
  const { showSuccess, showWarning } = useNotificationContext();
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const [changeBucketvisible, setChangeBucketVisible] = useState(false);
  const [temporaryTransferVisible, setTemporaryTransferVisible] =
    useState(false);
  const [terminateVisible, setTerminateVisible] = useState(false);
  const [suspensionVisible, setSuspensionVisible] = useState(false);
  const { filterEntity } = useFilterPersonContext();

  // QUERY DECLARATION
  const getPersonFilter = useCallback(() => {
    return personId ? { user_code: personId } : undefined;
  }, [personId]);

  const {
    data: employeeLedgerResponse,
  } = useQyGetEmployeeLedgerIndividual(
    "",
    1,
    0,
    undefined,
    getPersonFilter()
  );

  const {
    data: disciplinaryResponse,
  } = useQyGetEmployeeDiciplinary(
    "",
    1,
    0,
    undefined,
    getPersonFilter()
  );

  // API AVERAGE SCORE
  const { data: scoreResponse } = useQyGetEmployeeScoreAverage(
    personId,
    !!personId
  );
  const averageScore = scoreResponse?.data?.[0];

  // API DELETE Person
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Person deleted successfully");
  };
  const { mutate: deletePerson } = useQyDeletePerson(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const getActions = useCallback(() => {
    const deleteActionItem = {
      label: "Delete",
      type: "more",
      value: ActionEnum.Delete,
    } as ButtonAction;

    const actions: ButtonAction[] = [
      { label: "Print QR", type: "split", value: ActionEnum.NewQr },
      {
        label: "Permanent Transfer",
        type: "more",
        value: ActionEnum.ChangeBucket,
      },
      {
        label: "Temporary Transfer",
        type: "more",
        value: ActionEnum.TemporaryTransfer,
      },
      {
        label: "Suspend",
        type: "more",
        value: ActionEnum.Suspend,
      },
      {
        label: "Terminate",
        type: "more",
        value: ActionEnum.Offboard,
      },
    ];

    if (userIsManagement()) {
      actions.splice(0, 0, deleteActionItem);
    }

    return actions;
  }, [userIsManagement]);
  const handleBack = () => {
    if (returnParam === "event") {
      navigate(`/event`);
      return;
    } else if (returnParam === "employee") {
      navigate(`/personnel`);
      return;
    }

    navigate(`/branch/${branchId}`);
  };
  const accept = () => {
    if (!selectedPersonData) {
      throw new Error("No person data found");
    }

    const form = mapDeletePerson(selectedPersonData.code);
    deletePerson(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;

      case ActionEnum.NewQr:
        const bucketCode = selectedPersonData?.bucket.code;
        if (!bucketCode) {
          showWarning("No code found. Refresh the page and try again.");
          return;
        }

        setQrToken(bucketCode);
        setTimeout(() => {
          handlePrint();
        }, 500);
        break;

      case ActionEnum.ChangeBucket:
        setChangeBucketVisible(true);
        break;

      case ActionEnum.TemporaryTransfer:
        setTemporaryTransferVisible(true);
        break;

      case ActionEnum.Offboard:
        setTerminateVisible(true);
        break;

      case ActionEnum.Suspend:
        setSuspensionVisible(true);
        break;
    }
  };

  return {
    personId,
    selectedPersonData,
    isLoading,
    personError,
    formMethod,
    componentRef,
    qrToken,
    averageScore,
    changeBucketvisible,
    temporaryTransferVisible,
    terminateVisible,
    suspensionVisible,
    employeeLedgerResponse,
    disciplinaryResponse,
    setSuspensionVisible,
    setTerminateVisible,
    setTemporaryTransferVisible,
    setChangeBucketVisible,
    handleBack,
    handleAction,
    getValues,
    getActions,
  };
}
